import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { subscriptionApi } from '../utils/apiUtils';

const PaymentSuccess = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { fetchUser } = useAuth();
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const handleSuccess = async () => {
			try {
				const sessionId = searchParams.get('session_id');
				if (!sessionId) throw new Error('No session ID found');
				await subscriptionApi.checkoutSuccess(sessionId);
				await fetchUser();
				setTimeout(() => navigate('/user'), 3000);
			} catch (err) {
				console.error('Error processing payment:', err);
				setError('There was an error processing your payment. Please contact support.');
			} finally {
				setLoading(false);
			}
		};
		handleSuccess();
	}, [searchParams, fetchUser, navigate]);

	const Card = ({ children }) => (
		<motion.div
			className="max-w-sm mx-auto mt-20 bg-white rounded-2xl shadow-card-hover overflow-hidden border border-gray-100 text-center"
			initial={{ opacity: 0, y: 16 }}
			animate={{ opacity: 1, y: 0 }}
		>
			<div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />
			<div className="p-8">{children}</div>
		</motion.div>
	);

	if (loading) {
		return (
			<Card>
				<div className="w-10 h-10 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-5" />
				<h1 className="text-base font-bold text-gray-900 mb-1">Processing...</h1>
				<p className="text-xs text-gray-400">Confirming your transaction.</p>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<ExclamationCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-5" />
				<h1 className="text-base font-bold text-gray-900 mb-1">Payment Error</h1>
				<p className="text-xs text-gray-500 mb-5">{error}</p>
				<a href="mailto:support@paperdigest.xyz"
					className="inline-block bg-purple-600 text-white py-2 px-5 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-purple-700 transition-colors">
					Contact Support
				</a>
			</Card>
		);
	}

	return (
		<Card>
			<motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 150 }}>
				<CheckCircleIcon className="h-12 w-12 text-emerald-500 mx-auto mb-5" />
			</motion.div>
			<h1 className="text-lg font-bold text-gray-900 mb-2">Payment Successful!</h1>
			<p className="text-xs text-gray-500 mb-3">Your account has been upgraded.</p>
			<div className="h-px w-10 bg-gray-100 mx-auto mb-3" />
			<p className="text-[10px] text-gray-300">Redirecting to your dashboard...</p>
		</Card>
	);
};

export default PaymentSuccess;
